import type { GameAction } from "./actions";
import type { InputManager } from "./InputManager";

/**
 * Fraction du rayon parcourue par le manche à l'écran.
 *
 * Le doigt dispose de tout le rayon du socle pour atteindre l'inclinaison
 * maximale, mais le manche ne se déplace que de 56 % : ajouté à son propre
 * rayon (44 % du rayon du socle, voir `.joystick__knob` dans `styles.css`),
 * il affleure exactement le bord du socle sans jamais le déborder. Les deux
 * valeurs sont solidaires : modifier l'une demande d'ajuster l'autre.
 */
const KNOB_TRAVEL_RATIO = 0.56;

/** Rayon de repli si le socle n'est pas encore mesurable (élément non rendu). */
const FALLBACK_RADIUS = 64;

/** Sous ce seuil (fraction du rayon), le manche est considéré au repos. */
const DEFAULT_DEAD_ZONE = 0.15;

/**
 * Seuil de découplage des deux axes, en fraction de l'axe dominant.
 *
 * Un manche unique conduit *et* tourne : un pouce qui pousse « tout droit » ne
 * le fait jamais parfaitement, et sans ce filtre le cube dériverait en lacet à
 * chaque ligne droite. En deçà de 20 % de l'axe dominant, la composante mineure
 * est donc ramenée à zéro — soit un cône d'environ 11° autour de chaque axe.
 */
const DEFAULT_AXIS_SNAP = 0.2;

/**
 * Exposant de la courbe de réponse (1 = linéaire).
 *
 * La course utile d'un manche tactile ne fait que quelques centimètres :
 * étalée linéairement, le moindre millimètre change la vitesse. L'exposant
 * assouplit le début de course — là où l'on dose — sans rien retirer aux
 * extrêmes, qui restent exactement 0 et 1.
 */
const DEFAULT_RESPONSE_CURVE = 1.5;

/**
 * Actions publiées par les quatre directions du manche, nommées telles qu'on
 * les voit à l'écran. C'est ce qui rend le widget réutilisable : il ignore ce
 * que ses directions déclenchent, seule cette table le décide.
 */
export interface JoystickActions {
  readonly left: GameAction;
  readonly right: GameAction;
  readonly up: GameAction;
  readonly down: GameAction;
}

/**
 * Manche unique de conduite : l'axe vertical avance et recule, l'axe horizontal
 * fait pivoter. Les deux se cumulent — poussé en haut à droite, le manche fait
 * avancer le cube en le faisant virer à droite.
 */
export const DRIVE_JOYSTICK_ACTIONS: JoystickActions = {
  left: "turnLeft",
  right: "turnRight",
  up: "moveForward",
  down: "moveBackward",
};

export interface VirtualJoystickOptions {
  /** Élément hôte du widget. `document.body` par défaut. */
  readonly parent?: HTMLElement;
  /**
   * Zone morte exprimée en fraction du rayon, dans [0, 1[. Elle absorbe les
   * micro-déplacements du pouce : sans elle, poser le doigt suffirait à faire
   * dériver le cube.
   */
  readonly deadZone?: number;
  /**
   * Seuil de découplage des axes, en fraction de l'axe dominant, dans [0, 1[.
   * `0` laisse les deux axes bruts, au risque de tourner en croyant aller tout
   * droit.
   */
  readonly axisSnap?: number;
  /** Exposant de la courbe de réponse, ≥ 1. `1` rend la réponse linéaire. */
  readonly responseCurve?: number;
  /** Actions pilotées par le manche. La conduite par défaut. */
  readonly actions?: JoystickActions;
}

/**
 * Joystick virtuel affiché en bas à gauche de l'écran.
 *
 * Il ne connaît ni la scène ni le cube : il publie des intensités d'actions
 * dans l'`InputManager`, exactement comme le clavier. Une scène déjà écrite
 * pour ZQSD répond donc au joystick sans une ligne de plus, et l'inclinaison
 * du manche module la vitesse — ce que le clavier, tout ou rien, ne permet pas.
 *
 * Un seul manche pour tout conduire : le pouce gauche suffit, la moitié droite
 * de l'écran reste libre pour l'orbite caméra, et les deux axes se cumulent
 * naturellement (avancer en virant). En contrepartie, ils ne sont plus
 * indépendants comme sur deux manches : `track()` les remet en forme (zone
 * morte, courbe de réponse, découplage) pour que chaque geste reste lisible.
 *
 * Les actions publiées sont un paramètre : le widget se réutilise tel quel pour
 * un autre rôle — viser, piloter une tourelle — en changeant la seule table.
 *
 * Le suivi se fait en Pointer Events : souris, tactile et stylet passent par le
 * même chemin de code.
 */
export class VirtualJoystick {
  /** Racine du widget, montée dans le DOM à la construction. */
  readonly element: HTMLElement;

  private readonly input: InputManager;
  private readonly knob: HTMLElement;
  private readonly deadZone: number;
  private readonly axisSnap: number;
  private readonly responseCurve: number;
  private readonly actions: JoystickActions;

  /** Identifiant du pointeur qui tient le manche, `null` au repos. */
  private pointerId: number | null = null;
  private centerX = 0;
  private centerY = 0;
  private radius = FALLBACK_RADIUS;

  constructor(input: InputManager, options: VirtualJoystickOptions = {}) {
    this.input = input;
    this.deadZone = clamp(options.deadZone ?? DEFAULT_DEAD_ZONE, 0, 0.9);
    this.axisSnap = clamp(options.axisSnap ?? DEFAULT_AXIS_SNAP, 0, 0.9);
    // Sous 1, la courbe partirait à la verticale : le manche deviendrait plus
    // nerveux au centre qu'au bord, soit l'inverse du but recherché.
    this.responseCurve = Math.max(options.responseCurve ?? DEFAULT_RESPONSE_CURVE, 1);
    this.actions = options.actions ?? DRIVE_JOYSTICK_ACTIONS;

    this.knob = document.createElement("div");
    this.knob.className = "joystick__knob";

    this.element = document.createElement("div");
    // Le placement est affaire de CSS : le TypeScript n'a pas à connaître de
    // coordonnées.
    this.element.className = "joystick";
    // Commande redondante avec le clavier, et intrinsèquement gestuelle :
    // l'exposer aux technologies d'assistance n'apporterait rien d'utilisable.
    this.element.setAttribute("aria-hidden", "true");
    this.element.append(this.knob);

    (options.parent ?? document.body).append(this.element);

    this.element.addEventListener("pointerdown", this.handlePointerDown);
    window.addEventListener("pointermove", this.handlePointerMove);
    window.addEventListener("pointerup", this.handlePointerUp);
    window.addEventListener("pointercancel", this.handlePointerUp);
    // Un changement d'onglet ou de fenêtre pendant un appui ne produit pas de
    // pointerup : sans ce filet, le manche resterait incliné à vie.
    window.addEventListener("blur", this.handleBlur);
  }

  /** Le manche est-il tenu à cet instant ? */
  get isPressed(): boolean {
    return this.pointerId !== null;
  }

  dispose(): void {
    this.release();

    this.element.removeEventListener("pointerdown", this.handlePointerDown);
    window.removeEventListener("pointermove", this.handlePointerMove);
    window.removeEventListener("pointerup", this.handlePointerUp);
    window.removeEventListener("pointercancel", this.handlePointerUp);
    window.removeEventListener("blur", this.handleBlur);

    this.element.remove();
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    // Un seul pointeur à la fois : les doigts suivants doivent rester
    // disponibles pour la caméra.
    if (this.pointerId !== null) return;
    // Clic droit ou molette : rien à faire ici.
    if (event.pointerType === "mouse" && event.button !== 0) return;

    // Le socle ne bouge pas pendant l'appui : une mesure au début suffit, et
    // évite un reflow par évènement de déplacement.
    const rect = this.element.getBoundingClientRect();
    this.centerX = rect.left + rect.width / 2;
    this.centerY = rect.top + rect.height / 2;
    this.radius = rect.width > 0 ? rect.width / 2 : FALLBACK_RADIUS;

    this.pointerId = event.pointerId;
    this.element.classList.add("joystick--active");

    // Empêche la sélection de texte et les évènements souris de compatibilité
    // que le navigateur synthétiserait après un appui tactile.
    event.preventDefault();

    this.track(event.clientX, event.clientY);
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return;
    event.preventDefault();
    this.track(event.clientX, event.clientY);
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return;
    this.release();
  };

  private readonly handleBlur = (): void => {
    this.release();
  };

  /** Convertit la position du pointeur en intensités d'actions. */
  private track(clientX: number, clientY: number): void {
    const dx = clientX - this.centerX;
    // L'axe Y de l'écran descend ; celui du jeu avance.
    const dy = this.centerY - clientY;

    const distance = Math.hypot(dx, dy);
    const clamped = Math.min(distance, this.radius);

    // Direction unitaire. Au centre exact, il n'y en a pas : tout reste à zéro.
    const unitX = distance > 0 ? dx / distance : 0;
    const unitY = distance > 0 ? dy / distance : 0;

    // Le manche à l'écran suit le doigt sans mise en forme : le retour visuel
    // doit montrer où est le pouce, pas ce qu'on a décidé d'en garder.
    this.moveKnob(unitX * clamped * KNOB_TRAVEL_RATIO, unitY * clamped * KNOB_TRAVEL_RATIO);

    // La courbe s'applique à l'inclinaison, pas à chaque axe : la direction
    // pointée par le pouce est conservée telle quelle, seule sa force est dosée.
    const tilt = this.tiltFor(clamped);
    const x = unitX * tilt;
    const y = unitY * tilt;

    // Seul l'axe le plus faible est atténué : une poussée franche garde toute
    // sa force, une poussée presque droite perd son reliquat de travers.
    if (Math.abs(x) >= Math.abs(y)) {
      this.publish(x, this.decouple(y, x));
    } else {
      this.publish(this.decouple(x, y), y);
    }
  }

  /** Recentre le manche et rend la main au clavier. */
  private release(): void {
    if (this.pointerId === null) return;

    this.pointerId = null;
    this.element.classList.remove("joystick--active");
    this.moveKnob(0, 0);

    // Manche au centre : les quatre actions retombent à zéro par le même
    // chemin que le suivi, sans que le relâchement ait sa propre liste.
    this.publish(0, 0);
  }

  /**
   * Inclinaison utile du manche dans [0, 1] : zone morte retranchée, puis
   * courbe de réponse.
   */
  private tiltFor(distance: number): number {
    const ratio = this.radius > 0 ? distance / this.radius : 0;
    if (ratio <= this.deadZone) return 0;

    // L'échelle est redéployée sur [0, 1] : l'intensité repart de 0 au bord de
    // la zone morte au lieu d'y sauter.
    const tilt = (ratio - this.deadZone) / (1 - this.deadZone);

    return this.responseCurve === 1 ? tilt : tilt ** this.responseCurve;
  }

  /**
   * Atténue la composante mineure d'un manche tenu près d'un axe.
   *
   * Le seuil suit l'axe dominant plutôt qu'une valeur fixe : le filtre reste
   * donc un cône angulaire, également efficace manche à peine incliné ou à
   * fond, alors qu'une zone morte fixe étoufferait toutes les petites
   * commandes. La diagonale franche (`|minor| === |major|`) ressort intacte :
   * seul le voisinage de l'axe est ramené à zéro, et de façon continue — pas
   * de saut de commande au franchissement du seuil.
   */
  private decouple(minor: number, major: number): number {
    const threshold = this.axisSnap * Math.abs(major);
    const magnitude = Math.abs(minor);
    if (magnitude <= threshold) return 0;

    return Math.sign(minor) * ((magnitude - threshold) / (1 - this.axisSnap));
  }

  /** Publie l'inclinaison : chaque axe alimente deux actions opposées. */
  private publish(x: number, y: number): void {
    this.setStrength(this.actions.right, x);
    this.setStrength(this.actions.left, -x);
    this.setStrength(this.actions.up, y);
    this.setStrength(this.actions.down, -y);
  }

  /** Une action ne reçoit jamais de négatif : c'est son opposée qui prend le relais. */
  private setStrength(action: GameAction, value: number): void {
    this.input.setAnalogStrength(action, Math.max(value, 0));
  }

  private moveKnob(offsetX: number, offsetY: number): void {
    // `translate` (et non `left`/`top`) : la position est calculée par le
    // compositeur, sans reflow ni repaint du reste de la page.
    this.knob.style.translate = `${String(Math.round(offsetX))}px ${String(Math.round(-offsetY))}px`;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
